/**
 * TaskCreatedSuccessDialog - Modal de sucesso após criação de tarefa
 * 
 * Exibe confirmação visual com o número da tarefa gerada e opção
 * rápida para visualizar a tarefa recém-criada.
 * 
 * @version 1.1.0
 * RELEASE: 2026-10-09 v1.1.0 - Textos opcionais para reuso pela Ordem de Serviço
 * RELEASE: 2026-04-11 v1.0.0 - Criação inicial
 */

import { Check, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface TaskCreatedSuccessDialogProps {
  /** Controla visibilidade do modal */
  open: boolean;
  /** Callback para mudar estado de abertura */
  onOpenChange: (open: boolean) => void;
  /** Número sequencial da tarefa criada */
  taskNumber: number;
  /** Callback ao clicar em "Visualizar Tarefa" */
  onViewTask: () => void;
  /** v1.1.0 — textos opcionais para reuso (ex.: Ordem de Serviço) */
  title?: string;
  numberLabel?: string;
  /** Exibido no lugar de `#número` quando informado (ex.: OS-2026-0012) */
  numberText?: string;
  description?: string;
  viewLabel?: string;
}

export function TaskCreatedSuccessDialog({
  open,
  onOpenChange,
  taskNumber,
  onViewTask,
  title = 'Tarefa Criada com Sucesso!',
  numberLabel = 'Número da Tarefa',
  numberText,
  description = 'A tarefa foi criada e já está disponível no sistema. Você pode visualizá-la para adicionar mais detalhes ou andamentos.',
  viewLabel = 'Visualizar Tarefa',
}: TaskCreatedSuccessDialogProps) {
  const shown = numberText ?? `#${taskNumber}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm text-center" hideCloseButton>
        <DialogHeader className="flex flex-col items-center gap-4 pt-2">
          {/* Ícone de sucesso — círculo com gradiente verde */}
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/40 dark:to-emerald-800/40 ring-4 ring-emerald-100 dark:ring-emerald-900/30">
            <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" strokeWidth={3} />
          </div>

          <DialogTitle className="text-lg font-semibold">
            {title}
          </DialogTitle>

          <DialogDescription className="sr-only">
            {numberLabel} {shown}
          </DialogDescription>
        </DialogHeader>

        {/* Número da tarefa em destaque */}
        <div className="flex flex-col items-center gap-1 py-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            {numberLabel}
          </span>
          <span className="text-3xl font-bold text-foreground font-mono">
            {shown}
          </span>
        </div>

        {/* Texto descritivo */}
        <p className="text-sm text-muted-foreground px-4">
          {description}
        </p>

        {/* Botões de ação */}
        <div className="flex flex-col gap-2 pt-2 pb-1">
          <Button
            onClick={onViewTask}
            className="w-full gap-2"
          >
            <Eye className="w-4 h-4" />
            {viewLabel}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
